// Angular
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Third party
import moment from 'moment';

// Local library
import { IGetAccountReportExRequest, POOL_LIST_ID } from '@amwager/util-ts';

/**
 * Pure form + request helpers for the Statements view, so the
 * component keeps only template-facing handlers.
 */
@Injectable({ providedIn: 'root' })
export class StatementsService {
  private readonly formBuilder = inject(FormBuilder);

  // Pool options rendered in the Pool dropdown
  readonly poolList: string[] = POOL_LIST_ID;

  /**
   * Build the reactive form. Dates are real `Date` objects so the
   * mat-datepicker can round-trip them through its DateAdapter.
   * @returns {FormGroup} form with dateFrom, dateTo, track, pool
   */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      dateFrom: [moment().subtract(1, 'days').toDate(), [Validators.required]],
      dateTo: [new Date(), [Validators.required]],
      track: [''],
      pool: ['']
    });
  }

  /**
   * Map the form state to a statements API request
   * @param {FormGroup} form The statements form
   * @returns {IGetAccountReportExRequest} Request payload
   */
  buildRequest(form: FormGroup): IGetAccountReportExRequest {
    const value = form.getRawValue();
    return {
      startDate: value.dateFrom,
      endDate: value.dateTo,
      eventId: value.track || undefined,
      poolId: value.pool || undefined
    };
  }

  /**
   * True when "Date From" is after "Date To"
   * @param {FormGroup} form The statements form
   * @returns {boolean} Whether the range is invalid
   */
  isDateRangeInvalid(form: FormGroup): boolean {
    const from = form.get('dateFrom')?.value;
    const to = form.get('dateTo')?.value;
    if (!from || !to) return false;
    return moment(from).isAfter(moment(to));
  }
}
